<template lang="pug">
fieldset.birth-date-input.form-group(
  :disabled="disabled"
  :aria-describedby="describedBy"
  :aria-invalid="invalidState ? 'true' : null"
)
  legend.col-form-label.pt-0(v-if="label" :id="legendId")
    | {{ label }}
    span(v-if="required" aria-hidden="true")  *
  .birth-date-input__fields
    .birth-date-input__part.birth-date-input__part--short
      label.small.text-muted(:for="dayId") День
      input.form-control(
        ref="day"
        :id="dayId"
        type="text"
        inputmode="numeric"
        pattern="[0-9]*"
        maxlength="2"
        autocomplete="bday-day"
        placeholder="ДД"
        :value="day"
        :required="required"
        :disabled="disabled"
        :class="{'is-invalid': invalidState}"
        :aria-invalid="invalidState ? 'true' : null"
        :aria-describedby="describedBy"
        @input="updatePart('day', $event.target.value)"
      )
    .birth-date-input__part.birth-date-input__part--short
      label.small.text-muted(:for="monthId") Месяц
      input.form-control(
        ref="month"
        :id="monthId"
        type="text"
        inputmode="numeric"
        pattern="[0-9]*"
        maxlength="2"
        autocomplete="bday-month"
        placeholder="ММ"
        :value="month"
        :required="required"
        :disabled="disabled"
        :class="{'is-invalid': invalidState}"
        :aria-invalid="invalidState ? 'true' : null"
        :aria-describedby="describedBy"
        @input="updatePart('month', $event.target.value)"
      )
    .birth-date-input__part.birth-date-input__part--year
      label.small.text-muted(:for="yearId") Год
      input.form-control(
        ref="year"
        :id="yearId"
        type="text"
        inputmode="numeric"
        pattern="[0-9]*"
        maxlength="4"
        autocomplete="bday-year"
        placeholder="ГГГГ"
        :value="year"
        :required="required"
        :disabled="disabled"
        :class="{'is-invalid': invalidState}"
        :aria-invalid="invalidState ? 'true' : null"
        :aria-describedby="describedBy"
        @input="updatePart('year', $event.target.value)"
      )
  input(v-if="name" type="hidden" :name="name" :value="normalizedValue")
  small.form-text.text-muted(:id="hintId") Например, 31 03 1962
  .invalid-feedback.d-block(v-if="localValidationMessage" :id="errorId") {{ localValidationMessage }}
</template>

<script>
const {normalizeBirthDateParts, splitBirthDate} = require('../lib/birth-date')

export default {
  name: 'BirthDateInput',
  props: {
    value: {type: [String, Date], default: ''},
    name: {type: String, default: null},
    id: {type: String, default: null},
    label: {type: String, default: 'Дата рождения'},
    required: {type: Boolean, default: false},
    disabled: {type: Boolean, default: false},
    state: {type: Boolean, default: null},
  },
  data() {
    const parts = splitBirthDate(this.value)
    return {
      ...parts,
      generatedId: this.id || `birth-date-${this._uid}`,
      localValidationMessage: '',
    }
  },
  computed: {
    dayId() { return `${this.generatedId}-day` },
    monthId() { return `${this.generatedId}-month` },
    yearId() { return `${this.generatedId}-year` },
    legendId() { return `${this.generatedId}-legend` },
    hintId() { return `${this.generatedId}-hint` },
    errorId() { return `${this.generatedId}-error` },
    describedBy() {
      return [this.hintId, this.localValidationMessage ? this.errorId : null].filter(Boolean).join(' ')
    },
    today() {
      const now = new Date()
      const year = now.getFullYear()
      const month = String(now.getMonth() + 1).padStart(2, '0')
      const day = String(now.getDate()).padStart(2, '0')
      return `${year}-${month}-${day}`
    },
    normalizedResult() {
      return normalizeBirthDateParts({day: this.day, month: this.month, year: this.year}, this.today)
    },
    normalizedValue() {
      return this.normalizedResult.value
    },
    invalidState() {
      return this.state === false || Boolean(this.localValidationMessage)
    },
  },
  watch: {
    value(newValue) {
      if (newValue === this.normalizedValue) return

      Object.assign(this, splitBirthDate(newValue))
      this.$nextTick(this.validate)
    },
  },
  mounted() {
    this.validate()
  },
  methods: {
    updatePart(part, value) {
      const length = part === 'year' ? 4 : 2
      this[part] = String(value || '').replace(/\D/g, '').slice(0, length)
      this.validate()

      if (this.normalizedResult.valid && this.normalizedResult.complete) {
        this.$emit('input', this.normalizedValue)
      } else if (!this.day && !this.month && !this.year) {
        this.$emit('input', '')
      }
    },
    validate() {
      const result = this.normalizedResult
      if (!result.complete || result.valid) {
        this.localValidationMessage = ''
      } else if (result.reason === 'year') {
        this.localValidationMessage = 'Укажите год четырьмя цифрами'
      } else if (result.reason === 'future') {
        this.localValidationMessage = 'Дата рождения не может быть в будущем'
      } else {
        this.localValidationMessage = 'Введите существующую дату рождения'
      }

      const message = this.localValidationMessage
      ;['day', 'month', 'year'].forEach((part) => {
        if (this.$refs[part]) this.$refs[part].setCustomValidity(message)
      })
    },
  },
}
</script>

<style scoped lang="scss">
.birth-date-input {
  min-width: 0;

  &__fields {
    display: flex;
    align-items: flex-start;
  }

  &__part {
    margin-right: .5rem;

    &:last-child {
      margin-right: 0;
    }

    label {
      display: block;
      margin-bottom: .25rem;
    }

    &--short {
      width: 4.5rem;
    }

    &--year {
      width: 6rem;
    }
  }
}
</style>
